import { withExtraConfig } from '@studiometa/js-toolkit';
import { ModalWithTransition } from '@studiometa/ui';

/**
 * VideoModal component
 *
 * Modale vidéo rendue depuis une section, avec trois écarts par rapport à
 * `ModalWithTransition` : elle est remontée hors de `<main>` (voir `mounted()`),
 * elle lance la lecture à l'ouverture et la coupe à la fermeture — sans ça la
 * vidéo continue de tourner, et de faire du son, derrière la modale fermée,
 * quel que soit le moyen de fermeture (bouton, overlay ou touche Échap, qui
 * passent tous par `close()`).
 *
 * Le snippet `corep6-modal` doit être rendu avec `plyr_autoplay: false` : sinon
 * `VideoPlyr` démarre la vidéo dès son montage, donc au chargement de la page,
 * une modale en `fixed inset-0` étant « visible » pour l'IntersectionObserver
 * même fermée.
 */
export default class VideoModal extends withExtraConfig(ModalWithTransition, {
  name: 'VideoModal',
}) {
  /**
   * Move the modal out of `<main>`.
   *
   * `<main>` porte `relative z-1` : il ouvre un contexte d'empilement qui
   * enferme tout ce qui est rendu depuis une section sous le header (`z-30`),
   * y compris cette modale en `z-50` — la croix de fermeture se retrouve alors
   * masquée par le header. Les autres modales du thème sont rendues depuis le
   * layout pour cette raison ; une section ne pouvant pas le faire, on remonte
   * l'élément sur `<body>`, qui porte le composant racine : l'instance reste
   * donc rattachée à l'arbre de composants.
   *
   * On déplace `$el` nous-mêmes plutôt que d'utiliser l'option `move` de
   * `Modal` : son getter `$refs` fait `$refs[key] = ref` sur les refs
   * sauvegardées, alors que js-toolkit 3 renvoie un objet en lecture seule —
   * la moindre ref absente du markup fait lever un TypeError au montage.
   */
  mounted() {
    super.mounted();

    if (this.$el.parentElement !== document.body) {
      document.body.append(this.$el);
    }
  }

  /**
   * @returns {HTMLVideoElement|null}
   */
  get video() {
    return this.$el.querySelector('video');
  }

  /**
   * Start playing once the modal is open.
   * @returns {Promise<void>}
   */
  async open() {
    await super.open();
    this.video?.play();
  }

  /**
   * Pause the video before the closing transition, so the sound stops on click
   * rather than at the end of the animation.
   * @returns {Promise<void>}
   */
  async close() {
    this.video?.pause();

    return super.close();
  }
}
